import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Spline from '@splinetool/react-spline';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Utility function to detect Android devices
function isAndroidDevice() {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

type HeroProps = {
  onNavigate?: (page: 'home' | 'services' | 'teams' | 'contact', anchor?: string) => void;
  sectionRef?: React.RefObject<HTMLElement>;
};

// Wrap Hero in React.memo for performance
const Hero: React.FC<HeroProps> = React.memo(({ onNavigate, sectionRef }) => {
  const localSectionRef = useRef<HTMLElement>(null);
  const sectionToUse = sectionRef || localSectionRef;
  const contentRef = useRef<HTMLDivElement>(null);
  const splineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionToUse.current || !contentRef.current) return;

    // Parallax effect for content
    gsap.to(contentRef.current, {
      yPercent: -50,
      ease: "none",
      scrollTrigger: {
        trigger: sectionToUse.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    });

    // Scale effect for Spline object
    gsap.to(splineRef.current, {
      scale: 1.2,
      ease: "none",
      scrollTrigger: {
        trigger: sectionToUse.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [sectionToUse]);

  return (
    <section 
      ref={sectionToUse}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-purple-50"
      // Optimization: Hint browser to optimize transform/opacity changes
      style={{ willChange: 'transform, opacity' }}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-blue-50/15 to-purple-50/20 z-10 hidden md:block" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-blue-50/50 to-purple-50/60 z-10 md:hidden" />
      
      {/* Spline 3D Object */}
      {/**
       * Spline is only rendered on non-Android devices (PC, iOS, iPad, etc.)
       * This prevents performance issues on Android phones.
       */}
      {!isAndroidDevice() && (
      <div 
        ref={splineRef}
        className="absolute inset-0 z-0 hidden md:block"
        style={{ 
            willChange: 'transform, opacity', // Optimization
          transform: 'scale(1.1)',
          filter: 'blur(0px)',
          opacity: 1
        }}
      >
        <Spline
          scene="https://prod.spline.design/3Rq6MXsl4xUppirP/scene.splinecode"
          style={{
            width: '100%',
            height: '100%',
            cursor: 'grab'
          }}
        />
      </div>
      )}

      {/* Mobile Background */}
      <div className="absolute inset-0 z-0 md:hidden bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100" />

      {/* Content */}
      <div 
        ref={contentRef}
        className="relative z-20 text-center px-4 max-w-4xl mx-auto
          sm:px-4
          px-2
          pt-16 pb-12
        "
        style={{ willChange: 'transform, opacity' }} // Optimization
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-6"
          style={{ willChange: 'transform, opacity' }} // Optimization
        >
          <span className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
            Welcome to Zeoqo
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-3xl sm:text-5xl md:text-7xl font-black text-gray-900 mb-4 md:mb-6 leading-tight px-2 sm:px-4 tracking-tight"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', willChange: 'transform, opacity' }} // Optimization
        >
          We Create
          <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Digital Magic
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-base sm:text-xl md:text-2xl text-black font-semibold mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed px-2 sm:px-4"
          style={{ fontFamily: 'JetBrains Mono, monospace', willChange: 'transform, opacity' }} // Optimization
        >
          Transform your ideas into stunning digital experiences with our cutting-edge design and development expertise.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center px-2 sm:px-4"
          style={{ willChange: 'transform, opacity' }} // Optimization
        >
          <button
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-4 sm:py-4 rounded-full text-lg sm:text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            onClick={() => onNavigate && onNavigate('contact')}
          >
            Start Your Project
          </button>
          <button
            className="bg-white text-blue-700 border-2 border-blue-600 px-6 sm:px-8 py-4 sm:py-4 rounded-full text-lg sm:text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            onClick={() => onNavigate && onNavigate('teams', 'projects')}
          >
            View Our Work
          </button>
        </motion.div>
      </div>
    </section>
  );
});

export default Hero; 